<script setup>
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();

defineProps({
  strategies: {
    type: Array,
    required: true,
  },
});

const emit = defineEmits(['edit', 'delete']);

const getFormattedText = (strategy) => {
  return `${strategy.id_campanha} - ${strategy.text || ''}`;
};

const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    useAlert(t('STRATEGIES.COPY_SUCCESS'));
  } catch (err) {
    console.error('Failed to copy:', err);
  }
};
</script>

<template>
  <div class="bg-n-solid-2 rounded-xl border border-n-weak overflow-hidden">
    <!-- Header -->
    <div class="px-6 py-4 border-b border-n-weak bg-n-solid-3">
      <div class="flex items-center justify-between">
        <h2 class="text-lg font-semibold text-n-slate-12">
          {{ t('STRATEGIES.LIST_TITLE') }}
        </h2>
        <span class="text-sm text-n-slate-11">
          {{ t('STRATEGIES.TOTAL_COUNT', { count: strategies.length }) }}
        </span>
      </div>
    </div>

    <!-- Table -->
    <div class="overflow-x-auto">
      <table class="w-full">
        <thead>
          <tr class="border-b border-n-weak bg-n-alpha-1">
            <th class="px-4 py-3 text-left text-xs font-medium text-n-slate-11 uppercase tracking-wider">
              {{ t('STRATEGIES.TABLE.ID') }}
            </th>
            <th class="px-4 py-3 text-left text-xs font-medium text-n-slate-11 uppercase tracking-wider">
              {{ t('STRATEGIES.TABLE.NAME') }}
            </th>
            <th class="px-4 py-3 text-left text-xs font-medium text-n-slate-11 uppercase tracking-wider">
              {{ t('STRATEGIES.TABLE.TEXT') }}
            </th>
            <th class="px-4 py-3 text-left text-xs font-medium text-n-slate-11 uppercase tracking-wider">
              {{ t('STRATEGIES.TABLE.SHORT_LINK') }}
            </th>
            <th class="px-4 py-3 text-right text-xs font-medium text-n-slate-11 uppercase tracking-wider">
              {{ t('STRATEGIES.TABLE.ACTIONS') }}
            </th>
          </tr>
        </thead>
        <tbody class="divide-y divide-n-weak">
          <tr
            v-for="strategy in strategies"
            :key="strategy.id"
            class="hover:bg-n-alpha-1 transition-colors"
          >
            <td class="px-4 py-4 text-sm text-n-slate-11">
              {{ strategy.id_campanha }}
            </td>
            <td class="px-4 py-4">
              <span class="text-sm font-medium text-n-slate-12">
                {{ strategy.name }}
              </span>
            </td>
            <td class="px-4 py-4">
              <div class="flex items-center gap-2">
                <span class="text-sm text-n-slate-11 line-clamp-2 max-w-md">
                  {{ getFormattedText(strategy) }}
                </span>
                <button
                  v-if="strategy.text"
                  class="p-1 hover:bg-n-alpha-2 rounded flex-shrink-0"
                  :title="t('STRATEGIES.COPY_TEXT')"
                  @click="copyToClipboard(getFormattedText(strategy))"
                >
                  <span class="i-lucide-copy size-4 text-n-slate-11" />
                </button>
              </div>
            </td>
            <td class="px-4 py-4">
              <div v-if="strategy.short_link" class="flex items-center gap-2">
                <a
                  :href="strategy.short_link"
                  target="_blank"
                  class="text-sm text-n-brand hover:underline truncate max-w-xs"
                >
                  {{ strategy.short_link }}
                </a>
                <button
                  class="p-1 hover:bg-n-alpha-2 rounded"
                  @click="copyToClipboard(strategy.short_link)"
                >
                  <span class="i-lucide-copy size-4 text-n-slate-11" />
                </button>
              </div>
              <span v-else class="text-sm text-n-slate-10">-</span>
            </td>
            <td class="px-4 py-4">
              <div class="flex items-center justify-end gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  icon="i-lucide-pencil"
                  @click="emit('edit', strategy)"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  icon="i-lucide-trash-2"
                  color-scheme="alert"
                  @click="emit('delete', strategy)"
                />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
